import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  inject,
  signal,
  ChangeDetectorRef,
} from '@angular/core';
import { FooterComponent } from 'nebula-ui-kit';
import { filter, Subscription } from 'rxjs';
import { AuthService, TranslatePipe, TranslationService } from '@nebula-shared/data-access';
import { NavigationEnd, Router } from '@angular/router';

type FooterVariant = 'default' | 'login';

@Component({
  selector: 'app-footer-wrapper',
  templateUrl: './footer-wrapper.component.html',
  styleUrls: ['./footer-wrapper.component.scss'],
  standalone: true,
  imports: [FooterComponent, TranslatePipe],
})
export class FooterWrapperComponent implements OnInit, OnDestroy {
  // Inputs
  @Input() variant: FooterVariant = 'default';
  @Input() defaultTenantName = '';
  @Input() displayTenant = true;

  // Services
  private readonly authService = inject(AuthService);
  private readonly translation = inject(TranslationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly router = inject(Router);

  // Signals
  tenantName = signal<string>('');
  showTenant = signal<boolean>(true);
  isConnected = false;

  /** Fecha del footer; se renueva al cambiar idioma para reformatear con el nuevo locale. */
  readonly now = signal(new Date());

  // Subscriptions and intervals
  readonly subs = new Subscription();
  private connectionCheckInterval: ReturnType<typeof setInterval> | undefined;

  ngOnInit(): void {
    // 1. Load tenant from localStorage
    this.loadTenantFromStorage();

    // 2. If no tenant in localStorage, use defaultTenantName
    if (!this.tenantName()) {
      this.tenantName.set(this.defaultTenantName || '');
    }

    // 3. Check current route to determine if tenant should be shown
    this.checkCurrentRoute();

    // 4. Check connection status
    this.checkConnection();

    // 5. Set up periodic checks
    this.setupPeriodicChecks();

    // 6. Listen to language changes
    this.subs.add(
      this.translation.currentLang$.subscribe(() => {
        this.now.set(new Date());
        this.cdr.detectChanges();
      }),
    );

    // 7. Listen to storage changes
    this.listenToStorageChanges();

    // 8. Listen to custom events
    this.listenToCustomEvents();

    // 9. Listen to route changes for tenant refresh
    this.listenToRouteChanges();

    // 10. Force initial change detection
    this.cdr.detectChanges();
  }

  /**
   * Load tenant name from localStorage
   * Priority: companyName > user_data.companyName > user_data.tenant
   */
  private loadTenantFromStorage(): void {
    if (typeof window === 'undefined') return;

    let tenant = '';

    // Try to get from companyName key
    tenant = localStorage.getItem('companyName') || '';

    // If not found, try to get from user_data
    if (!tenant) {
      const userData = localStorage.getItem('user_data');
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          tenant = parsedData?.companyName || parsedData?.tenant || '';
        } catch (error) {
          console.error('Error parsing user_data:', error);
        }
      }
    }

    // Update tenantName signal
    if (tenant) {
      this.tenantName.set(tenant);
      // Ensure companyName is set in localStorage for consistency
      localStorage.setItem('companyName', tenant);
    } else {
      // If no tenant found, try to use default
      if (this.defaultTenantName) {
        this.tenantName.set(this.defaultTenantName);
      }
    }

    this.cdr.detectChanges();
  }

  /**
   * Check current route to determine if tenant should be displayed
   */
  private checkCurrentRoute(): void {
    const currentUrl = this.router.url;
    const shouldHide = currentUrl.includes('/company') || currentUrl === '/company';
    this.showTenant.set(!shouldHide);
  }

  /**
   * Setup periodic checks for connection status and tenant updates
   */
  private setupPeriodicChecks(): void {
    this.connectionCheckInterval = setInterval(() => {
      // Check connection
      this.checkConnection();

      this.subs.add(this.translation.currentLang$.subscribe(() => this.now.set(new Date())));

      // Refresh tenant data periodically
      this.loadTenantFromStorage();

      // Check current route
      this.checkCurrentRoute();

      // Trigger change detection
      this.cdr.detectChanges();
    }, 30000); // Check every 30 seconds
  }

  /**
   * Check connection status based on user_data in localStorage
   */
  private checkConnection(): void {
    if (typeof window === 'undefined') {
      this.isConnected = false;
      return;
    }

    const userData = localStorage.getItem('user_data');
    this.isConnected = userData !== null && userData !== undefined && userData !== '';
  }

  /**
   * Get connection status text
   */
  get connectionStatus(): string {
    return this.translation.translate(this.isConnected ? 'LAYOUT.CONN_OK' : 'LAYOUT.CONN_FAIL');
  }

  /**
   * Listen to storage changes from other tabs/windows
   */
  private listenToStorageChanges(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', this.handleStorageChange);
    }
  }

  /**
   * Handle storage changes
   */
  private handleStorageChange = (event: StorageEvent): void => {
    if (event.key === 'user_data' || event.key === 'companyName') {
      this.loadTenantFromStorage();
      this.checkConnection();
      this.cdr.detectChanges();
    }
  };

  /**
   * Listen to custom events (companySelected, tenantSelected, etc.)
   */
  private listenToCustomEvents(): void {
    if (typeof window !== 'undefined') {
      window.addEventListener('companySelected', this.handleCompanySelected as EventListener);
      window.addEventListener('tenantSelected', this.handleCompanySelected as EventListener);
    }
  }

  /**
   * Handle custom events for company/tenant selection
   */
  private handleCompanySelected = (event: CustomEvent): void => {
    if (!event.detail) return;

    let tenant = '';

    // Extract tenant from different possible event structures
    if (event.detail.companyName) {
      tenant = event.detail.companyName;
    } else if (event.detail.tenant) {
      tenant = event.detail.tenant;
    } else if (event.detail.name) {
      tenant = event.detail.name;
    } else if (typeof event.detail === 'string') {
      tenant = event.detail;
    }

    if (tenant) {
      this.tenantName.set(tenant);
      localStorage.setItem('companyName', tenant);
      this.cdr.detectChanges();
    } else {
      // If no tenant in event, reload from storage
      this.loadTenantFromStorage();
    }

    this.checkConnection();
  };

  /**
   * Listen to route changes to refresh tenant when navigating
   */
  private listenToRouteChanges(): void {
    this.subs.add(
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
        // Refresh tenant data on route change
        this.refreshTenantData();
        this.checkCurrentRoute();
        this.cdr.detectChanges();
      }),
    );
  }

  /**
   * Public method to refresh tenant data
   * Can be called from parent components
   */
  public refreshTenantData(): void {
    this.loadTenantFromStorage();
    this.checkConnection();
    this.checkCurrentRoute();
    this.cdr.detectChanges();
  }

  /**
   * Public method to manually update tenant
   * Can be called from parent components
   */
  public updateTenant(tenant: string): void {
    if (!tenant) return;

    // Update signal
    this.tenantName.set(tenant);

    // Update localStorage
    localStorage.setItem('companyName', tenant);

    // Dispatch event for other components
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('tenantUpdated', {
        detail: { tenant, source: 'footer-wrapper' },
      });
      window.dispatchEvent(event);
    }

    this.cdr.detectChanges();
  }

  /**
   * Public method to get current tenant name
   * Can be called from parent components
   */
  public getCurrentTenant(): string {
    return this.tenantName();
  }

  /**
   * Public method to get display tenant
   */
  public getDisplayTenant(): string {
    return this.tenantName() || this.defaultTenantName || '';
  }

  /**
   * Clean up on component destruction
   */
  ngOnDestroy(): void {
    // Clear interval
    if (this.connectionCheckInterval) {
      clearInterval(this.connectionCheckInterval);
    }

    // Remove event listeners
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', this.handleStorageChange);
      window.removeEventListener('companySelected', this.handleCompanySelected as EventListener);
      window.removeEventListener('tenantSelected', this.handleCompanySelected as EventListener);
    }

    // Unsubscribe from all subscriptions
    this.subs.unsubscribe();
  }
}
